import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authApi, tokenStore, setUnauthorizedHandler, apiErrorMessage } from "@/api/client";
import { teardownPush } from "@/native/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = checking, null = logged out, object = user
  const [user, setUser] = useState(undefined);

  const logout = useCallback(async () => {
    try { await teardownPush({ revoke: true }); } catch (_) { /* native-only */ }
    try { if (tokenStore.get?.()) await api.post("/visitor-team/offline"); } catch (_) { /* not an operator */ }
    try {
      await authApi.logout();
    } catch (_) {
      /* stateless token; ignore */
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      tokenStore.clear();
      setUser(null);
    });
    const t = tokenStore.get();
    if (!t) {
      setUser(null);
      return;
    }
    authApi
      .me()
      .then((r) => setUser(r.data.user))
      .catch(() => {
        tokenStore.clear();
        setUser(null);
      });
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await authApi.login(email, password);
      tokenStore.set(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: apiErrorMessage(e, "Invalid credentials") };
    }
  };

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isManager = ["admin", "super_admin", "sales_manager"].includes(user?.role);
  const canManageLeads = ["admin", "super_admin", "sales_manager", "sales_user"].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, isAdmin, isManager, canManageLeads }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
